import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ChainId, SearchResult } from '@pleco/shared';
import {
  CHAINS,
  formatCompact,
  formatPercent,
  formatUsd,
  gradeColor,
  isChainId,
  isValidAddress,
  riskColor,
  shortAddress,
} from '@pleco/shared';
import { api, type CompareRow } from '../lib/api.js';
import { useChains } from '../lib/chains.js';

interface Slot {
  chain: ChainId;
  address: string;
}

const MAX = 4;

/** `?t=sol:<mint>,sol:demo:clean` — demo addresses keep their own colon. */
function parseSlots(raw: string | null): Slot[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX)
    .flatMap((s) => {
      const i = s.indexOf(':');
      const chain = s.slice(0, i);
      return i > 0 && isChainId(chain) ? [{ chain, address: s.slice(i + 1) }] : [];
    });
}

type Better = 'high' | 'low';
interface Metric {
  key: keyof CompareRow;
  label: string;
  fmt: (v: number) => string;
  better?: Better;
  hint?: string;
}

const METRICS: { group: string; rows: Metric[] }[] = [
  {
    group: 'Verdict',
    rows: [
      { key: 'score', label: 'Decentralization score', fmt: (v) => v.toFixed(1), better: 'high', hint: '0–100, higher is more distributed' },
      { key: 'risk', label: 'Risk score', fmt: (v) => String(Math.round(v)), better: 'low', hint: '0–100, higher is riskier' },
      { key: 'effectiveEntities', label: 'Independent entities', fmt: (v) => v.toFixed(1), better: 'high', hint: '1 / HHI after collapsing clusters' },
    ],
  },
  {
    group: 'Concentration',
    rows: [
      { key: 'largestCluster', label: 'Largest cluster', fmt: (v) => formatPercent(v), better: 'low' },
      { key: 'clustered', label: 'Clustered supply', fmt: (v) => formatPercent(v), better: 'low' },
      { key: 'clusters', label: 'Clusters detected', fmt: (v) => String(v) },
      { key: 'top10Adjusted', label: 'Top 10 (organic)', fmt: (v) => formatPercent(v), better: 'low' },
      { key: 'top10', label: 'Top 10 (all)', fmt: (v) => formatPercent(v), better: 'low' },
      { key: 'gini', label: 'Gini coefficient', fmt: (v) => v.toFixed(3), better: 'low' },
    ],
  },
  {
    group: 'Launch forensics',
    rows: [
      { key: 'bundles', label: 'Bundled supply', fmt: (v) => formatPercent(v), better: 'low' },
      { key: 'snipers', label: 'Sniped supply', fmt: (v) => formatPercent(v), better: 'low' },
      { key: 'freshWallets', label: 'Fresh-wallet supply', fmt: (v) => formatPercent(v), better: 'low' },
    ],
  },
  {
    group: 'Market',
    rows: [
      { key: 'marketCapUsd', label: 'Market cap', fmt: (v) => formatUsd(v) },
      { key: 'liquidityUsd', label: 'Liquidity', fmt: (v) => formatUsd(v), better: 'high' },
      { key: 'holders', label: 'Holders indexed', fmt: (v) => formatCompact(v) },
      { key: 'cex', label: 'On exchanges', fmt: (v) => formatPercent(v) },
      { key: 'dex', label: 'In DEX pools', fmt: (v) => formatPercent(v) },
    ],
  },
];

export function ComparePage() {
  const { defaultChain } = useChains();
  const [params, setParams] = useSearchParams();
  const slots = useMemo(() => parseSlots(params.get('t')), [params]);
  const [rows, setRows] = useState<CompareRow[]>([]);
  const [best, setBest] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const setSlots = (next: Slot[]) => {
    const p = new URLSearchParams(params);
    if (next.length) p.set('t', next.map((s) => `${s.chain}:${s.address}`).join(','));
    else p.delete('t');
    setParams(p, { replace: true });
  };

  useEffect(() => {
    if (slots.length < 2) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    api
      .compare(slots)
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        setBest(r.best);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Compare failed'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slots]);

  const add = (r: SearchResult) => {
    if (slots.length >= MAX) return;
    if (slots.some((s) => s.chain === r.chain && s.address.toLowerCase() === r.address.toLowerCase())) return;
    setSlots([...slots, { chain: r.chain, address: r.address }]);
  };
  const remove = (i: number) => setSlots(slots.filter((_, j) => j !== i));

  /** Index of the best value in a metric row, or -1 when not comparable. */
  const winner = (m: Metric): number => {
    if (!m.better) return -1;
    const vals = rows.map((r) => (r.ok && typeof r[m.key] === 'number' ? (r[m.key] as number) : NaN));
    const valid = vals.filter((v) => !Number.isNaN(v));
    if (valid.length < 2 || new Set(valid).size === 1) return -1;
    const target = m.better === 'high' ? Math.max(...valid) : Math.min(...valid);
    return vals.indexOf(target);
  };

  return (
    <div className="app-body">
      <div className="main-col scroll-page">
        <div className="page-wrap">
          <h2 className="page-title">Compare tokens</h2>
          <p className="muted page-sub">
            Up to {MAX} tokens side by side, mapped with identical settings so the numbers are directly comparable.
            The strongest value in each row is highlighted.
          </p>

          <div className="compare-picker">
            {slots.map((s, i) => (
              <span key={`${s.chain}:${s.address}`} className="compare-slot">
                <i style={{ background: CHAINS[s.chain]?.color }} />
                {rows[i]?.symbol ?? shortAddress(s.address, 8, 4)}
                <button onClick={() => remove(i)} title="Remove">
                  ✕
                </button>
              </span>
            ))}
            {slots.length < MAX && <TokenAdder onPick={add} />}
            {slots.length === 0 && (
              <button
                className="btn sm"
                onClick={() =>
                  setSlots([
                    { chain: defaultChain, address: 'demo:clean' },
                    { chain: defaultChain, address: 'demo:insider' },
                    { chain: defaultChain, address: 'demo:bundle' },
                  ])
                }
              >
                Try with demo tokens
              </button>
            )}
          </div>

          {slots.length < 2 && <div className="empty-state">Add at least two tokens to compare.</div>}
          {error && <div className="statusbar error">{error}</div>}
          {loading && (
            <div className="empty-state">
              <span className="spinner" /> Building {slots.length} maps…
            </div>
          )}

          {!loading && rows.length >= 2 && (
            <div className="compare-table-wrap">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th />
                    {rows.map((r) => {
                      const key = `${r.chain}:${r.address}`;
                      return (
                        <th key={key} className={best === key ? 'is-best' : ''}>
                          <div className="ct-head">
                            {r.logoUrl ? (
                              <img className="token-logo" src={r.logoUrl} alt="" />
                            ) : (
                              <div className="token-logo">{(r.symbol ?? '?').slice(0, 3)}</div>
                            )}
                            <div>
                              <div className="ct-sym">
                                {r.symbol ?? shortAddress(r.address)}
                                {best === key && <span className="chip accent">best</span>}
                              </div>
                              <div className="faint" style={{ fontSize: 11 }}>
                                {CHAINS[r.chain]?.label}
                              </div>
                            </div>
                          </div>
                          {r.ok ? (
                            <div className="ct-grade">
                              <span className="chip" style={{ color: gradeColor(r.grade ?? ''), borderColor: gradeColor(r.grade ?? '') }}>
                                {r.grade}
                              </span>
                              <span className="chip" style={{ color: riskColor(r.riskLevel ?? ''), borderColor: riskColor(r.riskLevel ?? '') }}>
                                {r.riskLevel} risk
                              </span>
                            </div>
                          ) : (
                            <div className="danger" style={{ fontSize: 11, marginTop: 6 }}>
                              {r.error}
                            </div>
                          )}
                          {r.partial && <div className="chip warn" style={{ marginTop: 6 }}>indexing…</div>}
                          <Link className="ct-open" to={`/map/${r.chain}/${r.address}`}>
                            Open map →
                          </Link>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map((g) => (
                    <GroupRows key={g.group} group={g.group} metrics={g.rows} rows={rows} winner={winner} />
                  ))}
                  <tr className="ct-group">
                    <td colSpan={rows.length + 1}>Top risk signals</td>
                  </tr>
                  <tr>
                    <td className="ct-label">Primary concerns</td>
                    {rows.map((r) => (
                      <td key={`${r.chain}:${r.address}`} className="ct-flags">
                        {r.topFlags?.length ? r.topFlags.map((f) => <div key={f}>• {f}</div>) : <span className="ok">None</span>}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GroupRows({
  group,
  metrics,
  rows,
  winner,
}: {
  group: string;
  metrics: Metric[];
  rows: CompareRow[];
  winner: (m: Metric) => number;
}) {
  return (
    <>
      <tr className="ct-group">
        <td colSpan={rows.length + 1}>{group}</td>
      </tr>
      {metrics.map((m) => {
        const w = winner(m);
        return (
          <tr key={String(m.key)}>
            <td className="ct-label" title={m.hint}>
              {m.label}
            </td>
            {rows.map((r, i) => {
              const v = r[m.key];
              return (
                <td key={`${r.chain}:${r.address}`} className={`mono ${i === w ? 'ct-win' : ''}`}>
                  {r.ok && typeof v === 'number' ? m.fmt(v) : '—'}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}

/** Inline search box that adds a token to the comparison. */
function TokenAdder({ onPick }: { onPick: (r: SearchResult) => void }) {
  const { hasEvm, hasSolana, evmChains } = useChains();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    const id = setTimeout(() => {
      api
        .search(term)
        .then(({ results }) => setResults(results.slice(0, 8)))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const submitRaw = () => {
    const t = q.trim();
    if (hasEvm && /^0x[0-9a-fA-F]{40}$/.test(t)) {
      onPick({ chain: evmChains[0]!, address: t.toLowerCase(), name: t, symbol: shortAddress(t) });
      setQ('');
    } else if (hasSolana && isValidAddress('sol', t)) {
      onPick({ chain: 'sol', address: t, name: t, symbol: shortAddress(t) });
      setQ('');
    }
  };

  return (
    <div className="compare-adder">
      <input
        className="search-input"
        style={{ paddingLeft: 12 }}
        placeholder={`+ Add token (name, symbol or ${hasEvm ? 'address' : 'mint address'})`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submitRaw()}
      />
      {results.length > 0 && (
        <div className="search-results">
          {results.map((r) => (
            <div
              key={`${r.chain}:${r.address}`}
              className="search-item"
              onClick={() => {
                onPick(r);
                setQ('');
                setResults([]);
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="sym">
                  {r.symbol}{' '}
                  <span className="chip" style={{ color: CHAINS[r.chain]?.color }}>
                    {CHAINS[r.chain]?.label ?? r.chain}
                  </span>
                </div>
                <div className="nm">{r.name}</div>
              </div>
              {r.marketCapUsd ? <div className="right">{formatUsd(r.marketCapUsd)}</div> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
